import React from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TabPanel from './TabPanel.js';
import TextImportTab from './TextImportTab.js';
import ImageImportTab from './ImageImportTab.js';
import RecipeForm from './RecipeForm.js';
import { useRecipeForm } from './useRecipeForm.js';
import { useRecipeImport } from './useRecipeImport.js';

// Add a spin animation for loading indicators
const spinAnimation = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

function AddRecipe({ user, inventory, onIngredientAdded, onRecipeAdded, onRecipeUpdated, recipe }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isEditMode = !!recipe;

  // Use custom hooks for form and import functionality
  const {
    recipeData,
    setRecipeData,
    error: formError,
    success: formSuccess,
    amountErrors,
    handleIngredientChange,
    handleAmountChange,
    addIngredient,
    removeIngredient,
    handleSubmit,
  } = useRecipeForm({
    recipe,
    user,
    inventory,
    onIngredientAdded,
    onRecipeAdded,
    onRecipeUpdated,
    navigate
  });

  const {
    tabValue,
    pasteContent,
    setPasteContent,
    isProcessing,
    parsedLines,
    showComparison,
    setShowComparison,
    imageFile,
    imagePreview,
    error: importError,
    success: importSuccess,
    handleTabChange,
    handlePasteSubmit,
    handleImageUpload,
    handleImageSubmit
  } = useRecipeImport({
    inventory,
    setRecipeData
  });

  return (
    <Container maxWidth="md">
      <style>{spinAnimation}</style>
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          {isEditMode ? 'Edit Recipe' : 'Add New Recipe'}
        </Typography>

        <Paper sx={{ mb: 3 }}>
          {!isEditMode && (
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Text Description" />
              <Tab label="Camera Upload" />
            </Tabs>
          )}

          {(!isEditMode && tabValue === 0) && (
            <TabPanel value={tabValue} index={0}>
              <TextImportTab
                pasteContent={pasteContent}
                setPasteContent={setPasteContent}
                isProcessing={isProcessing}
                handlePasteSubmit={handlePasteSubmit}
                showComparison={showComparison}
                parsedLines={parsedLines}
                inventory={inventory}
                setRecipeData={setRecipeData}
                setShowComparison={setShowComparison}
                error={importError}
                success={importSuccess}
              />
            </TabPanel>
          )}

          {(!isEditMode && tabValue === 1) && (
            <TabPanel value={tabValue} index={1}>
              <ImageImportTab
                isProcessing={isProcessing}
                handleImageUpload={handleImageUpload}
                handleImageSubmit={handleImageSubmit}
                showComparison={showComparison}
                parsedLines={parsedLines}
                inventory={inventory}
                setRecipeData={setRecipeData}
                setShowComparison={setShowComparison}
                imageFile={imageFile}
                imagePreview={imagePreview}
                error={importError}
                success={importSuccess}
              />
            </TabPanel>
          )}

          {/* If not showing the comparison, display the standard form */}
          {showComparison === false && (isEditMode || parsedLines.length > 0) && (
            <RecipeForm
              recipeData={recipeData}
              setRecipeData={setRecipeData}
              amountErrors={amountErrors}
              handleIngredientChange={handleIngredientChange}
              handleAmountChange={handleAmountChange}
              addIngredient={addIngredient}
              removeIngredient={removeIngredient}
              handleSubmit={handleSubmit}
              navigate={navigate}
              isEditMode={isEditMode}
              error={formError}
              success={formSuccess}
              isMobile={isMobile}
              inventory={inventory}
            />
          )}
        </Paper>
      </Box>
    </Container>
  );
}

export default AddRecipe; 